import { cloneElement, type MouseEvent, type ReactElement, type Ref } from 'react';
import { Tooltip } from '@customerio/pluma-components/react';
import { useReturnHomeHint } from './use-return-home-hint';

export const RETURN_HOME_HINT_COPY = 'Come back to Home anytime to pick up your setup where you left off.';

type HintTarget = ReactElement<{ onClick?: (event: MouseEvent<HTMLElement>) => void; ref?: Ref<HTMLElement> }>;

type OnboardingReturnHomeHintProps = {
  /** The Home nav item the hint points at. Rendered untouched while the hint is closed. */
  children: HintTarget;
};

/**
 * One-time tooltip on the Home nav item, opened programmatically after a CSV
 * import is submitted. Hover and focus never open it; Pluma's dismiss plugin
 * (outside press, Escape) and clicking Home close it for good.
 */
export function OnboardingReturnHomeHint({ children }: OnboardingReturnHomeHintProps) {
  const { isOpen, dismiss } = useReturnHomeHint();

  if (!isOpen) {
    return children;
  }

  return (
    <Tooltip
      content={RETURN_HOME_HINT_COPY}
      placement="right"
      isCustom={true}
      isOpen={true}
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen) {
          dismiss('closed');
        }
      }}
      floatingPluginOptions={{ hover: { enabled: false }, focus: { enabled: false } }}
    >
      {(context) =>
        cloneElement(children, {
          ref: context.reference.setReference,
          ...context.reference.getReferenceProps(),
          onClick: (event: MouseEvent<HTMLElement>) => {
            children.props.onClick?.(event);
            dismiss('home-click');
          },
        })
      }
    </Tooltip>
  );
}
